import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import Switch from '@/components/Switch/Switch';
import { toast } from '@/components/toast/Toast';
import { pricingUrl, TermsUrl } from '@/environment';
import FlashLightning from '@/images/FlashLightning.svg';
import { useAuth } from '@/services/auth';
import theme from '@/theme';
import { PlanGroup } from '@d-id/types';
import styled from '@emotion/styled';
import { Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useEnableNebulySettings } from '../../../hooks/useNebulySettings';
import { DisableConfirmationModal } from './DisableConfirmationModal';

const ToggleContainer = styled.div<{ isEnabled?: boolean }>`
    background-color: ${({ isEnabled, theme }) => (isEnabled ? theme.colors.success[100] : theme.colors.primary.bgLight)};
    border: solid 1px ${({ isEnabled, theme }) => (isEnabled ? theme.colors.success.main : theme.colors.primary.main)};
    color: ${({ theme }) => theme.colors.black[900]};
    padding: 20px 16px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 24px;
`;

const CheckBoxInfoText = styled(Typography)`
    font-size: 14px;
    line-height: 20px;
    width: 90%;
    color: ${({ theme }) => theme.colors.black[900]};
    font-weight: 500;
`;

const LinkText = styled.label`
    color: ${({ theme }) => theme.colors.links.main};
    cursor: pointer;
`;

const UpgradeText = styled.label`
    color: ${({ theme }) => theme.colors.primary.main};
    cursor: pointer;
`;

const AgentInsightsToggle = () => {
    const { user } = useAuth();
    const { isEnabled: isAgentInsightsEnabled = undefined, toggle: agentInsightsToggleHandler, isLoading } = useEnableNebulySettings();
    const [openModal, setOpenModal] = useState(false);
    const [isNextVisit, setIsNextVisit] = useState<boolean | null>(false);
    const hasCheckedVisited = useRef(false);

    const AGENTS_INSIGHTS_VISITED_KEY = `agentInsightsVisited-${user?.sub || 'noname'}`;

    useEffect(() => {
        if (hasCheckedVisited.current) return;
        hasCheckedVisited.current = true;

        const visited = localStorage.getItem(AGENTS_INSIGHTS_VISITED_KEY);
        if (visited) {
            setIsNextVisit(true);
        } else {
            localStorage.setItem(AGENTS_INSIGHTS_VISITED_KEY, 'true');
            setIsNextVisit(false);
        }
    }, []);

    const handleInsightToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
        const checked = e.target.checked;

        if (checked) {
            agentInsightsToggleHandler(e, checked);
        } else {
            setOpenModal(true);
        }
    };

    const handleCancelDisable = () => {
        setOpenModal(false);
    };

    const handleConfirmDisable = async () => {
        await agentInsightsToggleHandler(null, false);
        toast.success({ title: 'Insights disabled' });
        localStorage.removeItem(AGENTS_INSIGHTS_VISITED_KEY);
        setOpenModal(false);
    };

    if (isLoading) {
        return null;
    }

    if (isNextVisit && isAgentInsightsEnabled) {
        return null;
    }

    return (
        <>
            <ToggleContainer isEnabled={isAgentInsightsEnabled}>
                <Switch checked={!!isAgentInsightsEnabled} onChange={handleInsightToggle} />
                <CheckBoxInfoText>
                    {isAgentInsightsEnabled
                        ? 'Your insights are populating. They will show here once you have available data.'
                        : 'Unlock Agent insights by agreeing to the '}
                    {!isAgentInsightsEnabled && <LinkText onClick={() => window.open(TermsUrl, '_blank')}>terms and conditions</LinkText>}
                </CheckBoxInfoText>
            </ToggleContainer>

            <DisableConfirmationModal
                open={openModal}
                onClose={handleCancelDisable}
                onCancel={handleCancelDisable}
                onSave={handleConfirmDisable}
            />
        </>
    );
};

export const EnableToggle = () => {
    const { user } = useAuth();
    const isFreeUser = user?.planGroup === PlanGroup.TRIAL || user?.planGroup === PlanGroup.FREE;

    if (!user) {
        return null;
    }

    if (isFreeUser) {
        return (
            <ToggleContainer isEnabled={false}>
                <MaskedIcon src={FlashLightning} size="24px" color={theme.colors.primary.main} />
                <CheckBoxInfoText>
                    Discover key insights into your user behaviour.{' '}
                    <UpgradeText onClick={() => window.open(pricingUrl, '_blank')}>Upgrade now</UpgradeText>
                </CheckBoxInfoText>
            </ToggleContainer>
        );
    }

    return <AgentInsightsToggle />;
};
